/**
 * Admin Calendar Store
 * Manages calendar collection data for admin users with full CRUD operations
 */
import { defineStore } from "pinia";
import { ref, computed } from 'vue';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  Timestamp,
  onSnapshot
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../js/firebase";
import { logger } from '../services/loggerService';
import { schedulingRulesService } from '../services/schedulingRulesService';
import { withDefaultAssignees } from '../utils/eventAssignees';
import { batchDeleteSubcollections } from '../services/firestoreUtils';

// Firebase collection reference
const calendarCollectionRef = collection(db, "calendar");

export const useAdminCalendarStore = defineStore('adminCalendar', () => {
  //-------------------------------
  // STATE
  //-------------------------------
  /**
   * Object containing all calendar events keyed by ID
   * @type {Object}
   */
  const items = ref({});
  
  /**
   * Loading state for async operations
   * @type {Boolean}
   */
  const loading = ref(false);
  
  /**
   * Whether the store has completed initial data load
   * @type {Boolean}
   */
  const initialized = ref(false);

  /**
   * Unsubscribe function for the real-time listener
   * @type {Function|null}
   */
  let unsubscribe = null;

  //-------------------------------
  // GETTERS
  //-------------------------------
  
  /**
   * Get all calendar events as an array
   */
  const allEvents = computed(() => Object.values(items.value));
  
  /**
   * Get events for a specific project
   * @param {String} projectId 
   */
  const getEventsByProject = (projectId) => {
    return allEvents.value.filter(event => event.projectId === projectId);
  };
  
  /**
   * Get events for a specific company
   * @param {String} companyId 
   */
  const getEventsByCompany = (companyId) => {
    return allEvents.value.filter(event => event.companyId === companyId);
  };
  
  /**
   * Get events by type (production or travel)
   * @param {String} type 
   */
  const getEventsByType = (type) => {
    return allEvents.value.filter(event => event.type === type);
  };

  //-------------------------------
  // ACTIONS
  //-------------------------------
  
  /**
   * Initialize store and set up real-time listener for all calendar events
   */
  async function init() {
    if (initialized.value && unsubscribe) {
      logger.debug('[AdminCalendar] Store already initialized with active listener');
      return;
    }

    // Clean up any existing listener
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }

    loading.value = true;

    try {
      logger.info('[AdminCalendar] Initializing calendar store with real-time listener');

      // Set up real-time listener
      unsubscribe = onSnapshot(
        calendarCollectionRef,
        (snapshot) => {
          const eventsData = {};

          snapshot.forEach((doc) => {
            const data = doc.data();
            eventsData[doc.id] = {
              id: doc.id,
              ...data
              // Keep dates as Timestamps for proper handling by dateService
            };
          });

          items.value = eventsData;

          // Only log on first load or significant changes
          if (!initialized.value) {
            logger.info('[AdminCalendar] Store initialized with real-time listener', {
              eventCount: Object.keys(eventsData).length
            });
            initialized.value = true;
            loading.value = false;
          } else {
            logger.debug('[AdminCalendar] Real-time update received', {
              eventCount: Object.keys(eventsData).length
            });
          }
        },
        (error) => {
          logger.firestoreListenerError('[AdminCalendar] Real-time listener error:', error);
          loading.value = false;

          // Handle permission-denied errors (typically from auth token expiration)
          if (error.code === 'permission-denied') {
            logger.warn('[AdminCalendar] Permission denied - cleaning up listener. User may need to re-authenticate.');
            // Clean up the failed listener
            if (unsubscribe) {
              unsubscribe();
              unsubscribe = null;
            }
            // Reset state so re-initialization can be attempted
            initialized.value = false;
          }
        }
      );
    } catch (error) {
      logger.error('[AdminCalendar] Failed to initialize store:', {
        error: error.message
      });
      loading.value = false;
      throw error;
    }
  }
  
  /**
   * Fetch calendar events with optional filters
   * @param {Object} filters - { companyId, projectId, type, startDate, endDate }
   */
  async function fetchCalendarEvents(filters = {}) {
    loading.value = true;
    
    try {
      logger.info('[AdminCalendar] Fetching calendar events', { filters });
      
      let q = calendarCollectionRef;
      const constraints = [];
      
      if (filters.companyId) {
        constraints.push(where('companyId', '==', filters.companyId));
      }
      if (filters.projectId) {
        constraints.push(where('projectId', '==', filters.projectId));
      }
      if (filters.type) {
        constraints.push(where('type', '==', filters.type));
      }
      
      // Apply constraints if any
      if (constraints.length > 0) {
        q = query(calendarCollectionRef, ...constraints, orderBy('date', 'asc'));
      } else {
        q = query(calendarCollectionRef, orderBy('date', 'asc'));
      }
      
      const querySnapshot = await getDocs(q);
      const eventsData = {};
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        eventsData[doc.id] = {
          id: doc.id,
          ...data
          // Keep dates as Timestamps for proper handling by dateService
        };
        
        // Log first few events for debugging
        if (Object.keys(eventsData).length <= 3) {
          logger.debug('[AdminCalendar] Event loaded:', {
            id: doc.id,
            title: data.title,
            date: data.date,
            dateType: typeof data.date,
            hasToDate: data.date?.toDate ? true : false,
            type: data.type
          });
        }
      });
      
      // Merge with existing items or replace based on filters
      if (Object.keys(filters).length === 0) {
        items.value = eventsData;
      } else {
        // Merge partial results
        Object.assign(items.value, eventsData);
      }
      
      logger.info('[AdminCalendar] Events fetched', {
        count: Object.keys(eventsData).length
      });
      
      return eventsData;
    } catch (error) {
      logger.error('[AdminCalendar] Failed to fetch events:', {
        error: error.message,
        filters
      });
      throw error;
    } finally {
      loading.value = false;
    }
  }
  
  /**
   * Add a new calendar event
   * @param {Object} eventData - { title, projectId, companyId, type, date, dateEnd? }
   */
  async function addCalendarEvent(eventData) {
    try {
      logger.info('[AdminCalendar] Adding calendar event', { eventData });
      
      // Validate required fields (date is optional for clientSelectDate events or draft events)
      if (!eventData.title || !eventData.projectId || !eventData.type) {
        throw new Error('Missing required fields: title, projectId, and type are required');
      }
      
      // Prefill the crew for this production type (empty assignedUserIds only)
      const withCrew = withDefaultAssignees(
        eventData,
        await schedulingRulesService.getCachedSettings()
      );

      // Convert date to Timestamp
      const eventToAdd = {
        ...withCrew,
        date: eventData.date instanceof Date 
          ? Timestamp.fromDate(eventData.date)
          : eventData.date,
        dateEnd: eventData.dateEnd instanceof Date
          ? Timestamp.fromDate(eventData.dateEnd)
          : eventData.dateEnd || null
      };
      
      const docRef = await addDoc(calendarCollectionRef, eventToAdd);
      
      // Add to local store
      items.value[docRef.id] = {
        id: docRef.id,
        ...withCrew
      };
      
      logger.info('[AdminCalendar] Calendar event added', {
        id: docRef.id,
        title: eventData.title
      });
      
      return docRef.id;
    } catch (error) {
      logger.error('[AdminCalendar] Failed to add event:', {
        error: error.message,
        eventData
      });
      throw error;
    }
  }
  
  /**
   * Add multiple calendar events in a single batch write
   * @param {Array} eventsArray - Array of event data objects
   * @returns {Array} Array of created document IDs
   */
  async function addCalendarEventsBatch(eventsArray) {
    try {
      logger.info('[AdminCalendar] Adding calendar events batch', { count: eventsArray.length });

      if (!eventsArray || eventsArray.length === 0) {
        throw new Error('Events array is required and must not be empty');
      }

      const batch = writeBatch(db);
      const docRefs = [];
      const rules = await schedulingRulesService.getCachedSettings();

      for (const eventData of eventsArray) {
        if (!eventData.title || !eventData.projectId || !eventData.type) {
          throw new Error('Each event requires title, projectId, and type');
        }

        // Prefill the crew for this production type (empty assignedUserIds only)
        const withCrew = withDefaultAssignees(eventData, rules);

        const docRef = doc(calendarCollectionRef);
        const eventToAdd = {
          ...withCrew,
          date: eventData.date instanceof Date
            ? Timestamp.fromDate(eventData.date)
            : eventData.date,
          dateEnd: eventData.dateEnd instanceof Date
            ? Timestamp.fromDate(eventData.dateEnd)
            : eventData.dateEnd || null,
        };

        batch.set(docRef, eventToAdd);
        docRefs.push({ id: docRef.id, data: withCrew });
      }

      await batch.commit();

      // Add all to local store for optimistic UI
      for (const { id, data } of docRefs) {
        items.value[id] = { id, ...data };
      }

      const createdIds = docRefs.map(r => r.id);
      logger.info('[AdminCalendar] Calendar events batch added', {
        count: createdIds.length,
        ids: createdIds,
      });

      return createdIds;
    } catch (error) {
      logger.error('[AdminCalendar] Failed to add events batch:', {
        error: error.message,
        count: eventsArray?.length,
      });
      throw error;
    }
  }

  /**
   * Update an existing calendar event
   * @param {String} eventId - The event document ID
   * @param {Object} updates - Fields to update
   */
  async function updateCalendarEvent(eventId, updates) {
    try {
      logger.info('[AdminCalendar] Updating calendar event', { eventId, updates });
      
      if (!eventId) {
        throw new Error('Event ID is required');
      }
      
      // Convert dates to Timestamps if present
      const updatesToSave = { ...updates };
      if (updates.date instanceof Date) {
        updatesToSave.date = Timestamp.fromDate(updates.date);
      }
      if (updates.dateEnd instanceof Date) {
        updatesToSave.dateEnd = Timestamp.fromDate(updates.dateEnd);
      }
      
      const eventRef = doc(db, 'calendar', eventId);
      await updateDoc(eventRef, updatesToSave);
      
      // Update local store
      if (items.value[eventId]) {
        items.value[eventId] = {
          ...items.value[eventId],
          ...updates
        };
      }
      
      logger.info('[AdminCalendar] Calendar event updated', { eventId });
      
      return true;
    } catch (error) {
      logger.error('[AdminCalendar] Failed to update event:', {
        error: error.message,
        eventId,
        updates
      });
      throw error;
    }
  }
  
  /**
   * Delete a calendar event
   * @param {String} eventId - The event document ID
   */
  async function deleteCalendarEvent(eventId) {
    try {
      logger.info('[AdminCalendar] Deleting calendar event', { eventId });
      
      if (!eventId) {
        throw new Error('Event ID is required');
      }
      
      const eventRef = doc(db, 'calendar', eventId);
      await deleteDoc(eventRef);
      
      // Remove from local store
      delete items.value[eventId];
      
      logger.info('[AdminCalendar] Calendar event deleted', { eventId });
      
      return true;
    } catch (error) {
      logger.error('[AdminCalendar] Failed to delete event:', {
        error: error.message,
        eventId
      });
      throw error;
    }
  }
  
  /**
   * Delete all calendar events for a specific project, including subcollections
   * @param {String} projectId
   */
  async function deleteProjectEvents(projectId) {
    try {
      logger.info('[AdminCalendar] Deleting all events for project', { projectId });

      if (!projectId) {
        throw new Error('Project ID is required');
      }

      // Query all events for this project
      const q = query(calendarCollectionRef, where('projectId', '==', projectId));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        logger.info('[AdminCalendar] No events found for project', { projectId });
        return 0;
      }

      // Collect event IDs
      const eventIds = querySnapshot.docs.map(d => d.id);

      // Delete subcollections for all events (productionSchedules, activity)
      logger.info('[AdminCalendar] Deleting event subcollections', { count: eventIds.length });
      await batchDeleteSubcollections('calendar', eventIds, ['productionSchedules', 'activity']);

      // Delete event documents in batches
      const batch = writeBatch(db);
      querySnapshot.forEach((docSnapshot) => {
        batch.delete(docSnapshot.ref);
      });

      await batch.commit();

      // Remove from local store
      eventIds.forEach(id => {
        delete items.value[id];
      });

      logger.info('[AdminCalendar] Project events deleted', {
        projectId,
        count: eventIds.length
      });

      return eventIds.length;
    } catch (error) {
      logger.error('[AdminCalendar] Failed to delete project events:', {
        error: error.message,
        projectId
      });
      throw error;
    }
  }
  
  /**
   * Clear all cached data, reset initialization, and clean up listener
   */
  function reset() {
    // Clean up real-time listener
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
    items.value = {};
    loading.value = false;
    initialized.value = false;
    logger.info('[AdminCalendar] Store reset');
  }

  /**
   * Backfill future calendar events onto the staff Google calendar.
   * Admin-only callable. Defaults to a dry run — pass { dryRun: false } to act.
   * Paginated: keep calling with { startAfterId: result.nextCursor } until
   * nextCursor is null.
   * @param {Object} options - { dryRun, limit, startAfterId, force }
   * @returns {Promise<Object>} summary counts + details[]
   */
  async function backfillStaffCalendar(options = {}) {
    const fn = httpsCallable(getFunctions(), 'backfillStaffCalendarEvents');
    const { data } = await fn(options);
    logger.info('[AdminCalendar] Staff calendar backfill', {
      dryRun: data.dryRun,
      scanned: data.scanned,
      created: data.created,
      updated: data.updated,
      errors: data.errors
    });
    return data;
  }

  //-------------------------------
  // RETURN PUBLIC INTERFACE
  //-------------------------------
  return {
    // State
    items,
    loading,
    initialized,
    
    // Getters
    allEvents,
    getEventsByProject,
    getEventsByCompany,
    getEventsByType,
    
    // Actions
    init,
    fetchCalendarEvents,
    addCalendarEvent,
    addCalendarEventsBatch,
    updateCalendarEvent,
    deleteCalendarEvent,
    deleteProjectEvents,
    backfillStaffCalendar,
    reset
  };
});